import styled from '@emotion/styled'
import { colors } from '../../__styling/settings/colors'
import {
    SectionHeader,
    SectionHeaderProps,
} from '../../section-header/SectionHeader/SectionHeader'
import { themedValue } from '../../__styling/themed-value'
import { Landscape } from '../../cards/Landscape/Landscape'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { MediaMode } from '../../cards/CardMedia/CardMedia'
import { CSSObject } from '@emotion/css'
import { ThemeMargins } from '../../__styling/settings/metrics'

export interface StyledGridRowProps {
    canGrow?: boolean
    hasBackground?: boolean
}

export const GridStyles: CSSObject = {
    display: 'flex',
    width: '100%',
    textAlign: 'left',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
}

export const StyledGridHeaderRow = styled('div')<StyledGridRowProps>(
    GridStyles,
    ({ hasBackground, canGrow, theme }) => [
        {
            flex: canGrow ? '1 1 auto' : '0 1 auto',
        },
        hasBackground && {
            padding: theme.cards.outerMargin,
            backgroundColor: colors.white,
        },
    ],
)

export const StyledSectionHeader = styled(SectionHeader)<SectionHeaderProps>(
    (props) => ({
        borderBottom: props.heading
            ? themedValue(props.theme, {
                  perthnow: `1px solid ${colors.perthnow.greyMarble}`,
                  fallback: undefined,
              })
            : undefined,
        alignSelf: 'flex-start',
    }),
)

export const StyledGrid = styled('div')<{
    noStretch?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    as: string
}>(
    {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        margin: 0,
        width: '100%',
        lineHeight: 1.154,
    },
    ({ theme, verticalSpacing, noStretch }) => ({
        flex: `${noStretch ? 0 : 1} 1 auto`,
        marginBottom:
            verticalSpacing && calcRem(theme.margins[verticalSpacing]),
    }),
)

export const StyledGridRow = styled('div')<StyledGridRowProps>(
    {
        ...GridStyles,
        flexGrow: 1,
    },
    ({ hasBackground }) =>
        hasBackground && {
            backgroundColor: colors.white,
        },
)

export const StyledGridColumn = styled('div')({
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexBasis: '100%',
    flex: '0 0 auto',
    flexWrap: 'nowrap',
    margin: 0,
    alignItems: 'stretch',
})

interface StyledListProps {
    numberedList?: boolean
    removeBorderLast?: boolean // remove border from last item in list
}

export const StyledList = styled('ol')<StyledListProps>((props) => ({
    padding: 0,
    margin: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',

    // Remove top padding on the first item
    '&:first-child': {
        paddingTop: themedValue(props.theme, {
            thewest: 0,
            fallback: undefined,
        }),
    },

    // Dividers
    '& > *': [
        (props.theme.kind === 'thewest' || props.theme.kind === 'perthnow') && {
            '&:not(:last-child)::after': {
                content: `''`,
                width: '100%',
                height: 1,
                position: 'absolute',
                left: 0,
                bottom: 0,
                backgroundColor: themedValue(props.theme, {
                    thewest: colors.thewest.greyDolphin,
                    perthnow: colors.perthnow.greyMarble,
                    fallback: colors.black,
                }),
            },
        },

        props.theme.kind === 'sevennews' && {
            position: 'relative',

            '&::after': {
                content: `''`,
                width: '100%',
                height: 1,
                position: 'absolute',
                left: 0,
                bottom: 0,
                backgroundColor: colors.sevennews.greyMarble,
            },
        },
    ],
}))

interface StyledLandscapeItemProps {
    removeBorderLast?: boolean // remove border from last item in list
    mediaMode?: MediaMode
}

export const StyledLandscapeItem = styled(Landscape)<StyledLandscapeItemProps>(
    (props) => ({
        padding: calcRem(8, 8, 4, 0),

        '&:first-of-type div': {
            paddingTop: themedValue(props.theme, {
                thewest: 0,
                fallback: undefined,
            }),
        },
    }),
)
